// Reporting duties (AGENT-REPORTING; 13 §Reporting duties, founder-approved 2026-10-21): the
// shapes of `cell.reporting_duty` and of a duty's status, as the scorecard metric, the duty list
// and the agent's `kpis.read` rows carry them. The due-rule math is domain-kpi's
// (`engine/reporting-duty.ts`); `DueRule` here is its zod form, so a stored rule and the engine
// cannot drift.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import { ReportingMethod, ReportingRemind } from "./enums";
import { PeriodKey, Timestamp, Uuid, WallTime } from "./primitives";

const covers = z.enum(["current", "previous"]).optional();

/** When a duty is due, in company time (`calendar_config.timezone`). */
export const DueRule = z
  .discriminatedUnion("freq", [
    z.strictObject({ freq: z.literal("daily"), time: WallTime, covers }),
    z.strictObject({
      freq: z.literal("weekly"),
      /** ISO weekday: 1 = Monday … 7 = Sunday. */
      weekday: z.int().min(1).max(7),
      time: WallTime,
      covers,
    }),
    z.strictObject({
      freq: z.literal("monthly"),
      /** 1–31, clamped to the month's last day. */
      day: z.int().min(1).max(31),
      time: WallTime,
      covers,
    }),
  ])
  .meta({ id: "DueRule" });
export type DueRule = z.infer<typeof DueRule>;

export const ReportingDuty = z
  .object({
    company_id: Uuid,
    id: Uuid,
    seat_id: Uuid,
    metric_def_id: Uuid,
    method: ReportingMethod,
    due_rule: DueRule,
    /** Minutes before the due instant that the reminder (and a DE's wake-up) fires. */
    lead_minutes: z.int().min(0).max(1440),
    evidence_required: z.boolean(),
    remind: z.array(ReportingRemind).max(2),
    ...ActorShortColumns.shape,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "ReportingDuty" });
export type ReportingDuty = z.infer<typeof ReportingDuty>;

export const DutyState = z
  .enum(["blocked", "late", "reported", "due_soon", "scheduled"])
  .meta({ id: "DutyState" });
export type DutyState = z.infer<typeof DutyState>;

/** A duty's status now (Next due / Late / Blocked; Last report is the metric's `last_report`). */
export const ReportingStatus = z
  .object({
    duty_id: Uuid,
    method: ReportingMethod,
    due_rule: DueRule,
    /** "Weekly, Friday 17:00" (domain-kpi `describeDueRule`). */
    due_label: z.string(),
    evidence_required: z.boolean(),
    state: DutyState,
    next_due_at: Timestamp,
    next_period_key: PeriodKey,
    last_due_at: Timestamp.nullable(),
    last_period_key: PeriodKey.nullable(),
    late_since: Timestamp.nullable(),
    blocked_reason: z.string().nullable(),
  })
  .meta({ id: "ReportingStatus" });
export type ReportingStatus = z.infer<typeof ReportingStatus>;

/** The newest agent value on the metric waiting for review (a `kpi_value` approval). */
export const PendingReview = z
  .object({
    approval_id: Uuid,
    run_id: Uuid.nullable(),
    period_key: PeriodKey,
    value: z.number().nullable(),
    quantity: z.number().nullable(),
    requested_at: Timestamp,
    /** How many of the metric's values on the seat wait for review. */
    count: z.int().min(1),
  })
  .meta({ id: "PendingReview" });
export type PendingReview = z.infer<typeof PendingReview>;
